import { useState, useEffect, useCallback } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Shuffle, Users, CheckSquare, RotateCcw, KeyRound } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function EvaluatorsAdmin() {
  const { t } = useTranslation();
  const [evaluators, setEvaluators] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [perCandidate, setPerCandidate] = useState(3);
  const [assigning, setAssigning] = useState(false);
  const [lastAssign, setLastAssign] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPw, setResettingPw] = useState(false);

  const load = useCallback(async () => {
    const res = await api.get("/evaluators");
    setEvaluators(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const createEvaluator = async () => {
    if (!form.name || !form.email || !form.password) { toast.error(t("admin.evaluators.fill_all")); return; }
    setCreating(true);
    try {
      await api.post("/evaluators", form);
      toast.success(t("admin.evaluators.created"));
      setAddOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.evaluators.create_failed"));
    } finally {
      setCreating(false);
    }
  };

  const removeEvaluator = async (id, name) => {
    if (!window.confirm(t("admin.evaluators.confirm_remove", { name }))) return;
    await api.delete(`/evaluators/${id}`);
    toast.success(t("admin.evaluators.removed"));
    load();
  };

  const autoAssign = async () => {
    setAssigning(true);
    try {
      const res = await api.post("/assignments/auto-assign", { reviewers_per_candidate: Number(perCandidate) });
      setLastAssign(res.data);
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.evaluators.assign_failed"));
    } finally {
      setAssigning(false);
    }
  };

  const clearAssignments = async () => {
    if (!window.confirm(t("admin.evaluators.confirm_clear"))) return;
    await api.post("/assignments/clear");
    setLastAssign(null);
    toast.success(t("admin.evaluators.cleared"));
    load();
  };

  const openReset = (e) => { setNewPassword(""); setResetTarget(e); };

  const generatePw = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    const bytes = crypto.getRandomValues(new Uint8Array(14));
    setNewPassword(Array.from(bytes, (b) => chars[b % chars.length]).join(""));
  };

  const doReset = async () => {
    if (newPassword.trim().length < 6) { toast.error(t("admin.password.too_short")); return; }
    setResettingPw(true);
    try {
      await api.patch(`/evaluators/${resetTarget.id}/password`, { password: newPassword.trim() });
      toast.success(t("admin.evaluators.pw_updated", { name: resetTarget.name }));
      setResetTarget(null);
      setNewPassword("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.password.failed"));
    } finally {
      setResettingPw(false);
    }
  };

  if (evaluators === null) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("admin.evaluators.eyebrow")}</p>
          <h1 className="font-heading text-3xl font-semibold tracking-tight">{t("admin.evaluators.title")}</h1>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="h-11" data-testid="add-evaluator-btn"><UserPlus className="w-4 h-4 mr-2" /> {t("admin.evaluators.add")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("admin.evaluators.add")}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-sm mb-2 block">{t("admin.evaluators.full_name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="evaluator-name" className="h-11" /></div>
              <div><Label className="text-sm mb-2 block">{t("common.email")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="evaluator-email" className="h-11" /></div>
              <div><Label className="text-sm mb-2 block">{t("common.password")}</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="evaluator-password" className="h-11" /></div>
            </div>
            <DialogFooter>
              <Button onClick={createEvaluator} disabled={creating} data-testid="save-evaluator-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.evaluators.create")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Auto-assign panel */}
      <div className="bg-secondary border border-primary/10 rounded-xl p-6 mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Shuffle className="w-5 h-5 text-primary" />
          <h3 className="font-heading text-lg font-medium">{t("admin.evaluators.assign_title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
          <Trans i18nKey="admin.evaluators.assign_intro" components={{ b: <b /> }} />
        </p>
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <Label className="text-sm mb-2 block">{t("admin.evaluators.per_candidate")}</Label>
            <Input type="number" min={1} max={evaluators.length || 1} value={perCandidate}
              onChange={(e) => setPerCandidate(e.target.value)} className="h-11 w-40" data-testid="per-candidate-input" />
          </div>
          <Button onClick={autoAssign} disabled={assigning || evaluators.length === 0} className="h-11" data-testid="auto-assign-btn">
            {assigning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("admin.evaluators.assigning")}</> : <><CheckSquare className="w-4 h-4 mr-2" /> {t("admin.evaluators.assign")}</>}
          </Button>
          <Button variant="outline" onClick={clearAssignments} className="h-11" data-testid="clear-assign-btn">
            <RotateCcw className="w-4 h-4 mr-2" /> {t("common.clear")}
          </Button>
        </div>
        {evaluators.length === 0 && <p className="text-xs text-destructive mt-3">{t("admin.evaluators.need_one")}</p>}
        {lastAssign && (
          <div className="mt-4 text-sm bg-card border border-border rounded-lg p-4" data-testid="assign-result">
            <p className="font-medium">{lastAssign.message}</p>
            {lastAssign.distribution?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {lastAssign.distribution.map((d) => (
                  <span key={d.id} className="text-xs bg-muted rounded-full px-3 py-1">{d.name}: {d.assigned}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Evaluators list */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {evaluators.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground text-sm">
            <Users className="w-8 h-8 mx-auto mb-3 opacity-40" />
            {t("admin.evaluators.empty")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider text-left">
                <th className="py-4 px-6 font-medium">{t("admin.evaluators.col_evaluator")}</th>
                <th className="py-4 px-6 font-medium">{t("admin.evaluators.col_assigned")}</th>
                <th className="py-4 px-6 font-medium">{t("admin.evaluators.col_reviewed")}</th>
                <th className="py-4 px-6 font-medium text-right">{t("admin.evaluators.col_actions")}</th>
              </tr>
            </thead>
            <tbody>
              {evaluators.map((e) => (
                <tr key={e.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`evaluator-row-${e.id}`}>
                  <td className="py-4 px-6">
                    <p className="font-medium text-foreground">{e.name}</p>
                    <p className="text-xs text-muted-foreground">{e.email}</p>
                  </td>
                  <td className="py-4 px-6">{e.assigned_count}</td>
                  <td className="py-4 px-6">
                    <span className={e.reviewed_count >= e.assigned_count && e.assigned_count > 0 ? "text-primary font-medium" : ""}>
                      {e.reviewed_count} / {e.assigned_count}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right">
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-primary" onClick={() => openReset(e)} title={t("admin.evaluators.reset_tooltip")} data-testid={`reset-pw-${e.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={() => removeEvaluator(e.id, e.name)} data-testid={`delete-evaluator-${e.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setNewPassword(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("admin.evaluators.reset_title", { name: resetTarget?.name })}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-sm mb-1 block">{t("admin.password.new")}</Label>
            <div className="flex gap-2">
              <Input value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t("admin.evaluators.enter_or_generate")} className="h-11" data-testid="reset-pw-input" />
              <Button type="button" variant="outline" onClick={generatePw} className="h-11 shrink-0" data-testid="gen-pw-btn">{t("admin.evaluators.generate")}</Button>
            </div>
            <p className="text-xs text-muted-foreground">{t("admin.evaluators.reset_hint")}</p>
          </div>
          <DialogFooter>
            <Button onClick={doReset} disabled={resettingPw} data-testid="save-reset-pw-btn">
              {resettingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : t("admin.password.update")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
